
import React, { useState, useEffect } from "react";
import { DashboardNavbar } from "@/components/dashboard/DashboardNavbar";
import { NotificationSettings } from "@/components/dashboard/NotificationSettings";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";

const NotificationsPage = () => {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    notifyOnBooking: true,
    notifyOnCancellation: true,
    notifyOnReminder: true,
    emailAddress: "",
    phoneNumber: ""
  });
  const [logs, setLogs] = useState<any[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [businessId, setBusinessId] = useState<string | null>(null);

  useEffect(() => {
    document.title = "Notification Settings | Figgy.link";
    loadSettings();
  }, []);

  // Load logs once businessId is available
  useEffect(() => {
    if (businessId) loadLogs();
  }, [businessId]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Use limit(1) instead of .single() so duplicate business_profiles rows
      // (legacy upsert bug) don't cause a PGRST116 error.
      const { data: profiles, error } = await supabase
        .from('business_profiles')
        .select('id, notifications_enabled, admin_cancellation_notifications_enabled, reminders_enabled, email, phone')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) throw error;
      const profile = profiles?.[0];
      if (!profile) return;

      setBusinessId(profile.id);

      setSettings({
        ...settings,
        notifyOnBooking: profile.notifications_enabled ?? true,
        notifyOnCancellation: profile.admin_cancellation_notifications_enabled ?? true,
        notifyOnReminder: profile.reminders_enabled ?? true,
        emailAddress: profile.email || user.email || "",
        phoneNumber: profile.phone || ""
      });
    } catch (error: any) {
      console.error('Error loading notification settings:', error);
      toast.error('Failed to load notification settings');
    } finally {
      setLoading(false);
    }
  };
  
  const loadLogs = async () => {
    if (!businessId) return;
    try {
      setLogsLoading(true);
      // Show the most recent 50 client_messages entries for this business
      const { data, error } = await supabase
        .from('client_messages')
        .select('id, message_type, status, error_message, created_at')
        .eq('business_id', businessId)
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) throw error;
      setLogs(data || []);
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || 'Failed to load logs');
    } finally {
      setLogsLoading(false);
    }
  };
  
  const handleSaveSettings = async (updatedSettings: any) => {
    setSettings(updatedSettings);

    if (!businessId) {
      toast.error('Business profile not found');
      return;
    }

    try {
      const { error } = await supabase
        .from('business_profiles')
        .update({
          notifications_enabled: updatedSettings.notifyOnBooking ?? true,
          admin_cancellation_notifications_enabled: updatedSettings.notifyOnCancellation ?? true,
          reminders_enabled: updatedSettings.notifyOnReminder ?? true,
        })
        .eq('id', businessId);

      if (error) throw error;

      toast.success('Notification settings saved successfully');
    } catch (error: any) {
      console.error('Error saving notification settings:', error);
      toast.error('Failed to save notification settings');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardNavbar />
      <div className="pt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
            <div>
              <h1 className="text-2xl font-semibold">Notifications</h1>
              <p className="text-muted-foreground">
                Configure notification preferences for you and your clients
              </p>
            </div>
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-figgy-500"></div>
            </div>
          ) : (
            <>
              <NotificationSettings 
                initialSettings={settings}
                onSave={handleSaveSettings}
              />
              <div className="mt-8">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>Recent Notification Logs</CardTitle>
                    <button className="text-sm underline" onClick={loadLogs} disabled={logsLoading}>
                      {logsLoading ? 'Refreshing…' : 'Refresh'}
                    </button>
                  </CardHeader>
                  <CardContent>
                    {logs.length === 0 ? (
                      <div className="text-sm text-muted-foreground">No logs yet</div>
                    ) : (
                      <div className="space-y-2 max-h-96 overflow-auto">
                        {logs.map((l) => (
                          <div key={l.id} className="flex items-start justify-between border-b pb-2">
                            <div>
                              <div className="text-sm">
                                <Badge variant={l.message_type === 'reminder' ? 'secondary' : 'outline'} className="mr-2 text-[10px]">
                                  {l.message_type}
                                </Badge>
                                <Badge variant={l.status === 'sent' ? 'default' : l.status === 'failed' ? 'destructive' : 'secondary'} className="text-[10px]">
                                  {l.status}
                                </Badge>
                              </div>
                              {l.error_message && (
                                <div className="mt-1 text-xs text-red-500 break-all">{l.error_message}</div>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground ml-4 whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;
